import { keyPicker } from 'factions/metatagger'
import {
  CHARGE_PHASE,
  COMBAT_PHASE,
  DURING_GAME,
  END_OF_TURN,
  HERO_PHASE,
  MOVEMENT_PHASE,
  SHOOTING_PHASE,
  START_OF_ROUND,
  START_OF_TURN,
  WOUND_ALLOCATION_PHASE,
} from 'types/phases'
import CommandAbilities from './command_abilities'
import rule_sources from './rule_sources'
import { TItemDescriptions } from 'factions/factionTypes'

const Flavors = {
  'Coven of Blood': {
    effects: [
      {
        name: `The Breath of Khaine - Passive`,
        desc: `Effect: While each model in a friendly Daughters of Khaine unit is contesting an objective you control, those models are not visible to enemy models more than 9" away.`,
        when: [DURING_GAME],
      },
    ],
  },
  'Frenzied Devotees': {
    effects: [
      {
        name: `Heretic's Bane - Once Per Turn - Start of Enemy Turn`,
        desc: `Declare: Pick an enemy Wizard or Priest on the battlefield to be the target.
        Effect: Roll a dice for each objective you control. For each 3+, subtract 1 from casting rolls and chanting rolls for the target for the rest of turn.`,
        when: [START_OF_TURN],
      },
    ],
  },
  'Cold-Hearted Murderers': {
    effects: [
      {
        name: `Bloody-Handed Worshippers - Reaction: You declared a Fight ability for an anointed unit`,
        desc: `Used by: That anointed unit.
        Effect: Pick a friendly non-Hero Daughters of Khaine unit that has not used a Fight ability this turn and is within that anointed unit's combat range to be the target. The target can be picked to use a Fight ability immediately after the Fight ability used by that anointed unit has been resolved. If it is picked to do so, the target's melee weapons have Crit (Auto-wound) for the rest of the turn. If its melee weapons already have Crit (Auto-wound) they have Crit (Mortal) for the rest of the turn instead.`,
        when: [COMBAT_PHASE],
      },
    ],
  },
  'Fervent Ritualists': {
    effects: [
      {
        name: `Final Frenzy - Once Per Turn`,
        desc: `Declare: Pick each friendly Daughters of Khaine unit contesting an objective to be the targets.
        Effect: Return up to D3 slain models to each target that has a Health charcteristic of up to 2. Return up to 1 slain model to each target that has a Health characteristic of 3 or more.`,
        when: [END_OF_TURN],
      },
    ],
  },

  'Coven Zealots': {
    effects: [
      {
        name: `Higher Purpose - Once Per Turn`,
        desc: `Effect: Return D3 slain models to each friendly Daughters of Khaine Aelf Infantry unit.`,
        when: [END_OF_TURN],
      },
    ],
  },
  'Arena Veterans': {
    effects: [
      {
        name: `Graceful Elusion`,
        desc: `Effect: For the rest of the phase, friendly Daughters of Khaine Aelf Infantry units have Ward (5+) until they have used a Fight ability.`,
        when: [COMBAT_PHASE],
      },
    ],
  },
  'The Croneseers Pariahs (AoR)': {
    effects: [
      {
        name: `Guided by Morai-Hag - Passive`,
        desc: `Effect: Add 1 to the Rend characteristic of combat attacks made by friendly non-Hero Infantry units while they are wholly within 9" of a friendly Krethusa.`,
        when: [COMBAT_PHASE],
      },
      {
        name: `Plumes of Auspicious Smoke - Passive`,
        desc: `Effect: Friendly Cauldron of Blood units are either empty or full. They start the battle empty. 
        While a friendly Cauldron of Blood unit is full: 
        That Cauldron of Blood has Ward (4+) against damage points inflicted by shooting attacks. 
        Subtract 1 from hit rolls for shooting attacks that target friendly Croneseers Pariahs units while they are wholly within 9" of that Cauldron of Blood.`,
        when: [DURING_GAME, SHOOTING_PHASE],
      },
      {
        name: `Laden with Prophecy`,
        desc: `Declare: Pick a friendly empty Cauldron of Blood. 
        Effect: If any enemy models were slain this turn by that Cauldron of Bloods combat attacks, it becomes full.`,
        when: [END_OF_TURN],
      },
      {
        name: `Skilled Skirmishers - Once Per Turn`,
        desc: `Declare: Pick a friendly Croneseers Pariahs Cavalry unit that has not charged this turn and is in combat with an enemy unit that charged this turn to be the target. 
        Effect: Roll a dice. On 3+, the target can immediately use a Retreat ability as if it were your movement phase. In addition, no mortal damage is inflicted on the target by that Retreat ability.`,
        when: [CHARGE_PHASE],
      },
      {
        name: `The Blood Reveals All - Once Per Battle`,
        desc: `Declare: Pick a friendly Croneseers Pariahs Hero within the combat range of a friendly empty Cauldron of Blood. Then, pick another unit within the combat range of that Hero to be the target. 
        Effect: Roll a dice. Allocate a number of damage points to the target equal to the roll (ward rolls cannot be made for those damage points). Then, that Cauldron of Blood becomes full.`,
        when: [HERO_PHASE],
      },
    ],
  },
  'Champions of the Arena (AoR)': {
    effects: [
      {
        name: `Arena of Slaughter - Passive`,
        desc: `Effect: If the number of units (friendly or enemy) that have been destroyed this battle equals or exceeds any of the numbers below, the corresponding effect applies to friendly Champions of the Arena units for the rest of the battle. More than 1 effect can apply to a unit at the same time.
        1 Add 1 to run rolls for those units.
        2 Add 1 to charge rolls for those units.
        3 Add 1 to hit rolls for those units' combat attacks.
        4 Add 1 to wound rolls for those units' combat attacks.
        5 Add 1 to the Attacks characteristic of those units' melee weapons.`,
        when: [DURING_GAME],
      },
      {
        name: `Let the Blood Flow - Passive`,
        desc: `Effect: All effects of the Arena of Slaughter ability apply to the friendly Champions of the Arena units while they are wholly within X" of a friendly Shrine of Dark Tribute, where X is determined by the current battle round, as follows:
        Round X
        1 9"
        2 12"
        3 15"
        4 18"
        5 21"`,
        when: [DURING_GAME],
      },
      {
        name: `Command Ability: Frenzied Restoration - End of Turn`,
        desc: `Declare: Pick a friendly Champions of the Arena Hero that has been destroyed to be the target. 
        Effect: Set up a replacement unit identical to the target wholly within 12" of a friendly Shrine of Dark Tribute and more than 9" from all enemy units.`,
        when: [END_OF_TURN],
      },
      {
        name: `Explosive Fury - Once Per Turn`,
        desc: `Declare: Pick a friendly Slaughter Queen to use this ability, then pick each friendly Blood Hags unit on the battlefield and each visible friendly Champions of the Arena unit wholly within 12" of that Slaughter Queen to be the targets.
        Effect: For the rest of the turn, each time a target makes a pile-in move, add 3" to the distance it can move.`,
        when: [COMBAT_PHASE],
      },
    ],
  },
  'Zainthar Kai (AoR)': {
    effects: [
      {
        name: `Dark Majesty - Passive`,
        desc: `Effect: If the unmodified hit roll for an attack made by a non-Hero unit that targets the friendly Morathi-Khaine is 1-4, the attack fails and the attack sequence ends.`,
        when: [SHOOTING_PHASE, COMBAT_PHASE],
      },
      {
        name: `The Blood Saints - Passive`,
        desc: `Effect: While the friendly Shadow QUeen is wholly within 6" of a friendly non-Hero Zainthar Kai unit, both that Shadow Queen and that non-Hero unit have Ward (5+).`,
        when: [DURING_GAME],
      },
      {
        name: `Command Ability: Matriarch of the Zainthar Kai`,
        desc: `Declare: Pick the friendly Morathi-Khaine to use this ability.
        Effect: Pick 1 of the following effects:
        Until the start of your next turn, this unit has a Move characteristic of 12" and has Fly.
        This unit can use the Iron Heart of Khaine ability as if you had performed a blood rite.
        Pick a visible enemy unit within 18" of this unit to be the target. For the rest of the turn, the target's attacks cannot score critical hits (treat them as regular hits instead).
        Pick a friendly non-Unique Zainthar Kai Hero that has been destroyed to be the target. Set up a replacement unit identical to the target within 3" of this unit.`,
        when: [HERO_PHASE],
      },
      {
        name: `Cursed Heritage - Once Per Turn`,
        desc: `Declare: Pick each enemy unit in combat with any non-Unique Zainthar Kai units to be the targets.
        Effect: Inflict D3 mortal damage on each target.`,
        when: [END_OF_TURN],
      },
    ],
  },
  /*'Zainthar Kai': {
    effects: [
      {
        name: `Khaine's Essence`,
        desc: `Each time a model in a friendly ZAINTHAR KAI MELUSAI unit is slain by an attack made with a melee weapon, that model can fight before it is removed from play.`,
        when: [WOUND_ALLOCATION_PHASE],
        rule_sources: [rule_sources.BATTLETOME_DAUGHTERS_OF_KHAINE, rule_sources.ERRATA_JULY_2022],
      },
    ],
  }, */
} satisfies TItemDescriptions

export default Flavors
